// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { AdvisorRole } from '@shared/data-access/interfaces/api/engine/citec';
import { selectClientId } from '@shared/data-access/slices/clients';
import { selectRole } from '@shared/data-access/slices/user';
import { useAppSelector } from '@advisor-app/store/store';
import { useMemo } from 'react';

export const isAdvisorNotAgent = (role: AdvisorRole): boolean =>
  role === AdvisorRole.AdvisorNotAgent;

export const canUseTraderByRole = (role: AdvisorRole): boolean =>
  !isAdvisorNotAgent(role);

export const canSendProposalsByRole = (role: AdvisorRole): boolean =>
  !isAdvisorNotAgent(role);

export const useAdvisorRole = () => {
  const role = useAppSelector(selectRole);
  const activeClientId = useAppSelector(selectClientId);

  const isAdvisorNotAgentRole = useMemo(() => isAdvisorNotAgent(role), [role]);

  const canSendProposals = useMemo(() => canSendProposalsByRole(role), [role]);

  const canUseTrader = useMemo(
    () => canUseTraderByRole(role) && activeClientId !== null,
    [role, activeClientId],
  );

  return {
    role,
    isAdvisorNotAgent: isAdvisorNotAgentRole,
    canUseTrader,
    canSendProposals,
  };
};
